/**
 * Storefront -> /api/analytics/engagement bridge.
 *
 * Master prompt v2.1 / Phase 3: every product-card view, cart-add and
 * confirmed sale is reported to the DHC HQ engagement ledger. The HQ
 * route stamps the canonical conversion_weight server-side (VIEW=1,
 * CART_ADD=5, SALE=10) so this helper only needs to send the type and
 * source_origin.
 *
 *   - VIEW events are coalesced per session+sku to avoid hammering on
 *     repeated re-renders (one event per card per 5-minute window).
 *   - All fetches are best-effort and never throw to the UI.
 *   - The endpoint runs on the DHC HQ origin (`api` prop on the page);
 *     same-origin policy is satisfied via the existing API helper.
 */

import axios from "axios";

const SOURCE_INTERNAL = "CHEEPSWEEPZ_INTERNAL";
const VIEW_DEBOUNCE_MS = 5 * 60 * 1000;
const __viewLedger = new Map();

function shouldEmitView(productSku) {
  const key = String(productSku || "").toUpperCase();
  if (!key) return false;
  const now = Date.now();
  const prev = __viewLedger.get(key) || 0;
  if (now - prev < VIEW_DEBOUNCE_MS) return false;
  __viewLedger.set(key, now);
  return true;
}

async function postEngagement(api, payload) {
  try {
    await axios.post(`${api}/analytics/engagement`, payload, {
      withCredentials: true,
      timeout: 4000,
      headers: { "Content-Type": "application/json" },
    });
  } catch {
    // Engagement reporting is best-effort and must never disrupt UX.
  }
}

export function reportProductView({ api, productSku, productId, marketplace }) {
  if (!shouldEmitView(productSku)) return;
  return postEngagement(api, {
    product_sku: productSku,
    product_id: productId || null,
    interaction_type: "VIEW",
    source_origin: SOURCE_INTERNAL,
    metadata: {
      channel: "cheepsweepz.shop",
      marketplace: marketplace || null,
    },
  });
}

export function reportCartAdd({ api, productSku, productId, marketplace, quantity }) {
  return postEngagement(api, {
    product_sku: productSku,
    product_id: productId || null,
    interaction_type: "CART_ADD",
    source_origin: SOURCE_INTERNAL,
    event_count: Math.max(1, Math.trunc(Number(quantity) || 1)),
    metadata: {
      channel: "cheepsweepz.shop",
      marketplace: marketplace || null,
    },
  });
}

export function reportSale({ api, productSku, productId, marketplace, orderId }) {
  return postEngagement(api, {
    product_sku: productSku,
    product_id: productId || null,
    interaction_type: "SALE",
    source_origin: SOURCE_INTERNAL,
    metadata: {
      channel: "cheepsweepz.shop",
      marketplace: marketplace || null,
      order_id: orderId || null,
    },
  });
}
